import React, { useEffect, useCallback, useState, useMemo } from 'react';
import {
  Form,
  Col,
  Modal,
  Row,
  Select,
  Space,
  Input,
  InputNumber,
  Divider,
  Card,
  Typography,
  Image,
  Button,
  Radio
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import numeral from 'numeral';
import useAuthContext from '../../contexts/AuthContext';
import {
  isMetropolianAndMonacoZipcode,
  isValidZipcode
} from '../../utils/zipcode';
import useErrorMessage from '../../utils/ErrorMessage';

import useDefaultValues from './defaultValues';

import maxMedal from '../../assets/images/max-medal.svg';
import frenchFlag from '../../assets/images/french-flag.svg';
import useQuotesContext from './QuotesContext';

import {
  MRIWK_MRI_SURFACE_DEFAULT_VALUE,
  MRIWK_MRI_SURFACE_EXEMPTION_LIMIT_MIN,
  MRIWK_MRI_SURFACE_SUBSCRIPTION_LIMIT_MAX
} from './mriwkFields';
import { useDebounce } from '../../utils/useDebounce';
import SpinLoader from '../../components/SpinLoader/SpinLoader';

export const MRI_MODAL_PRODUCTS = ['MRI', 'MRIWK'];

const OfferCard = ({ offer, isLoading = false, isBetter = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setDataModal } = useQuotesContext();

  const isUnavailable = useMemo(() => {
    if (offer?.isAvailable) {
      return false;
    }
    return true;
  }, [offer]);

  const isDerogatory = useMemo(() => {
    return offer?.isDerogatory;
  }, [offer]);

  const CardExtra = useMemo(() => {
    if (offer?.supplier) {
      return (
        <Row justify="end" align="middle">
          <Space orientation="horizontal" align="center">
            <Typography.Text style={{ fontSize: 9 }}>
              {offer?.supplier}
            </Typography.Text>
            <div
              style={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Image
                src={frenchFlag}
                alt="french_flag"
                height={15}
                preview={false}
                style={{
                  width: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              />
            </div>
          </Space>
        </Row>
      );
    }
    return <div style={{ height: '22px', width: '100%' }} />;
  }, [offer?.supplier]);

  const goToCreate = () => {
    setDataModal({
      ...(offer?.defaultValues || {}),
      ...(offer?.data || {})
    });

    const { type } = offer;
    const link = `/quotes/${type}/create`;

    navigate(link);
  };

  const EstimationContent = useMemo(() => {
    if (isUnavailable) {
      return (
        <Row
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'start',
            fontSize: '18px'
          }}
        >
          <Typography.Text
            italic
            level={5}
            style={{ fontSize: '10px', textAlign: 'center' }}
          >
            {t('mri_offers_modal.unavailable')}
          </Typography.Text>
        </Row>
      );
    }
    if (isLoading || !offer?.estimation?.total_monthly_customer) {
      return (
        <Row
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '18px'
          }}
        >
          <SpinLoader />
        </Row>
      );
    }
    return (
      <>
        <Row justify="center">
          {!isDerogatory && (
            <Typography.Text>
              {t(`mri_offers_modal.price_starting_from`)}
            </Typography.Text>
          )}
        </Row>
        <Row justify="center">
          <Typography.Text
            style={{
              fontSize: 18,
              borderBottom: '1px solid',
              borderRadius: '1px',
              borderColor: `${isBetter ? 'gold' : 'lightgray'}`,
              boxShadow: `0 4px 2px -2px ${isBetter ? 'gold' : 'lightgray'}`
            }}
          >
            {!isDerogatory
              ? t(`mri_offers_modal.monthly_estimation`, {
                  price: numeral(
                    offer?.estimation?.total_monthly_customer
                  ).format('0,0.00')
                })
              : t('choice_offer_modal.derogatory_offer')}
          </Typography.Text>
        </Row>
      </>
    );
  }, [isLoading, isUnavailable, offer]);

  return (
    <Card
      size="small"
      extra={CardExtra}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: '6px',
        boxShadow: '0 2px 2px rgb(0 0 0 / 20%)',
        border: '2px solid',
        borderColor: isBetter ? '#0C5787' : 'transparent'
      }}
      styles={{
        body: {
          padding: '8px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        },
        header: {
          padding: '0 2px',
          border: 'none'
        }
      }}
    >
      <Row justify="center">
        <Typography.Title level={5} underline style={{ marginBottom: 4 }}>
          {offer?.label}
        </Typography.Title>
      </Row>

      <Row justify="center">
        <Typography.Text
          italic
          level={5}
          style={{ fontSize: '10px', marginBottom: 12 }}
        >
          {t('mri_offers_modal.subtitle', {
            context: isBetter && !isDerogatory ? 'better' : ''
          })}
        </Typography.Text>
      </Row>

      <Col
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        {EstimationContent}
      </Col>

      <Row justify="center" style={{ marginTop: '18px' }}>
        <Button
          type="primary"
          style={{
            position: '',
            bottom: '5px',
            width: '90%',
            color: 'lightgray'
          }}
          onClick={goToCreate}
          disabled={isLoading || (isUnavailable && !isDerogatory)}
        >
          <span>{t('mri_offers_modal.go_to_quote')}</span>
        </Button>
      </Row>
    </Card>
  );
};

const defaultFormValues = {
  zipcode: '',
  city: null,
  street: null,
  number: null,
  building_type: 'FULL_HOUSE',
  surface_area: 100,
  specific_risk: false,
  is_vacant: false
};

const MRIMultiOffersModal = ({ isOpen = false, onClose }) => {
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();
  const { getEstimation } = useQuotesContext();

  const [form] = Form.useForm();
  const zipcodeWatcher = Form.useWatch('zipcode', form);
  const contractTypeWatcher = 'MRI';
  const valuesWatcher = Form.useWatch([], form);
  const debouncedFormValues = useDebounce(valuesWatcher, 200);

  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [productsOffers, setProductsOffers] = useState([]);
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [addressSuggestion, setAddressSuggestion] = useState([]);

  const getAddressAutocomplete = async (address_autocomplete) => {
    if (address_autocomplete) {
      setIsLoadingAddress(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${address_autocomplete}`
        });
        setAddressSuggestion(data?.predictions);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoadingAddress(false);
    }
  };

  const zipcodeValidator = useCallback(
    (zipcode) =>
      !!zipcode &&
      isValidZipcode(zipcode) &&
      isMetropolianAndMonacoZipcode(zipcode),
    []
  );

  useEffect(() => {
    form.setFieldValue('surface_area', MRIWK_MRI_SURFACE_DEFAULT_VALUE);
  }, [contractTypeWatcher]);

  const productAssets = [
    {
      type: 'mriml',
      label: 'MILASSUR’IMMO',
      supplier: 'MILA',
      defaultValues: useDefaultValues({ type: 'mriml' }),
      estimationDataBuilder: (values, defaultValues = {}) => {
        return {
          ...defaultValues,
          buildings: [
            {
              address: {
                postal_code: values.zipcode,
                city: values.city,
                street: values.street,
                number: values.number
              }
            }
          ],
          surface_area: values.surface_area,
          guarantee_type: 'TYPE1',
          main_use: 'rec_DWELLING',
          occupation_trade: 'under_25'
        };
      },
      estimation: null,
      defaultPrice: 5.0,
      checkUnavailable: useCallback((isQPV) => isQPV[valuesWatcher])
    },
    {
      type: 'mriwk',
      label: 'MRI’ASSUR 2',
      supplier: 'WAKAM',
      defaultValues: useDefaultValues({ type: 'mriwk' }),
      estimationDataBuilder: (values, defaultValues = {}) => {
        return {
          ...defaultValues,
          building_type: 'BUILDING',
          address: {
            address_autocomplete: values.address_autocomplete.split('#').pop(),
            city: values.city,
            postal_code: values.zipcode,
            country: 'France',
            street: values.street,
            number: values.number
          },
          guarantee_package: 'PKG_1',
          surface_area: values.surface_area,
          contains_professional_activity: false
        };
      },
      estimation: null,
      defaultPrice: 5.0,
      checkUnavailable: useCallback(
        ({ surface_area, specific_risk }) => {
          if (specific_risk) return true;

          return (
            surface_area < MRIWK_MRI_SURFACE_EXEMPTION_LIMIT_MIN ||
            surface_area > MRIWK_MRI_SURFACE_SUBSCRIPTION_LIMIT_MAX
          );
        },
        [valuesWatcher]
      )
    }
  ];

  const computeEstimation = async (isQPV) => {
    setEstimationLoading(true);
    try {
      const values = form.getFieldsValue();

      let bestOfferType = null;
      let bestOfferPrice = null;

      const offers = await Promise.all(
        productAssets.map(async (asset) => {
          const { estimationDataBuilder, checkUnavailable, defaultValues } =
            asset;

          const data = estimationDataBuilder(values, defaultValues);
          let estimation = null;
          let isAvailable = false;
          let isDerogatory = false;

          const unavailable = checkUnavailable(values, isQPV);

          const shouldForceAvailability =
            (values.surface_area < 200 ||
              values.specific_risk ||
              values.is_vacant) &&
            isQPV &&
            asset.type === 'mriml';

          if (!unavailable || shouldForceAvailability) {
            estimation = await getEstimation(data, asset.type);

            if (!estimation?.error) {
              isAvailable = true;

              if (
                !bestOfferType ||
                estimation.total_monthly_customer < bestOfferPrice
              ) {
                bestOfferType = asset.type;
                bestOfferPrice = estimation.total_monthly_customer;
              }
            }
          }
          if (asset.type === 'mriwk') {
            delete data.address;
          } else if (values.surface_area > 2000) {
            isDerogatory = true;
          }

          // tmp update
          if (asset.type === 'mriml') {
            isDerogatory = true;
          }

          return {
            ...asset,
            isAvailable,
            estimation,
            data,
            isBetter: false,
            isDerogatory
          };
        })
      );

      if (bestOfferType) {
        const bestOffer = offers.find((o) => o.type === bestOfferType);
        if (bestOffer) bestOffer.isBetter = true;
      }
      // if (
      //   values.is_vacant &&
      //   offers.filter((o) => o.isAvailable && !o.isDerogatory).length >= 2
      // ) {
      //   const mrimlOffer = offers.find((o) => o.type === 'mriml');
      //   if (mrimlOffer) {
      //     mrimlOffer.isAvailable = false;
      //   }
      // }

      setEstimationLoading(false);
      setProductsOffers(offers);
      return offers;
    } catch (error) {
      setEstimationLoading(false);
      setProductsOffers([]);
      return [];
    }
  };

  const handleChange = async () => {
    const isZipcodeFieldTouched = form.isFieldTouched('zipcode');
    const city = form.getFieldValue('city');
    const street = form.getFieldValue('street');
    const number = form.getFieldValue('number');
    const zipcode = form.getFieldValue('zipcode');

    if (!zipcodeValidator(zipcodeWatcher) || !city) {
      setProductsOffers([]);
      return;
    }

    let isQPV = false;
    if (city && street && number && zipcode && isZipcodeFieldTouched) {
      try {
        const url = `/insee/qpv?number=${number}&street=${encodeURIComponent(
          street
        )}&city=${encodeURIComponent(city)}&zipcode=${zipcode}`;
        const response = await dispatchAPI('GET', { url });

        isQPV = response?.data?.isQPV || false;
      } catch (error) {
        isQPV = true;
      }
    }

    if (isZipcodeFieldTouched && city && street && number && zipcode) {
      await computeEstimation(isQPV);
    }
  };

  useEffect(() => {
    const validateAndHandle = async () => {
      try {
        await form.validateFields();
        handleChange();
        // eslint-disable-next-line no-unused-vars
      } catch (errorInfo) {}
    };

    validateAndHandle();
  }, [debouncedFormValues]);

  const getCities = async (zipcode) => {
    try {
      if (!zipcodeValidator(zipcode)) return [];
      const { data } = await dispatchAPI('GET', {
        url: `/wakam/cities?postal_code=${zipcode}`
      });
      return data.map((city) => ({ label: city, value: city }));
    } catch (e) {
      if (e.response) message(e.response.status);
      return [];
    }
  };

  const resetCities = () => {
    setCities([]);
    form.setFieldValue('city', '');
  };

  const getAddressPlaceId = async (placeId) => {
    setIsLoadingAddress(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/places/placeid/${placeId}`
      });
      if (data) {
        const postalCodeFind = data.result.address_components.find(
          (component) => component.types.includes('postal_code')
        )?.long_name;
        const numberFind = data.result.address_components.find((component) =>
          component.types.includes('street_number')
        )?.long_name;
        const streetFind = data.result.address_components.find((component) =>
          component.types.includes('route')
        )?.long_name;
        const formattedAddress = data.result.formatted_address;
        if (postalCodeFind) {
          await getCities(postalCodeFind)
            .then((c) => {
              if (Array.isArray(c) && c.length > 0) {
                setCities(c);
                form.setFieldValue('city', c[0].value);
                form.setFieldsValue({
                  formatted_address: formattedAddress,
                  zipcode: postalCodeFind,
                  street: streetFind,
                  number: numberFind
                });
              } else {
                resetCities();
              }
            })
            .catch(resetCities);
        }
      }
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoadingAddress(false);
  };

  useEffect(() => {
    setCitiesLoading(true);
    if (zipcodeValidator(zipcodeWatcher)) {
      getCities(zipcodeWatcher)
        .then((c) => {
          if (Array.isArray(c) && c.length > 0) {
            setCities(c);
            form.setFieldValue('city', c[0].value);
          } else {
            resetCities();
          }
        })
        .catch(resetCities);
    } else {
      resetCities();
    }
    setCitiesLoading(false);
  }, [zipcodeWatcher]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    form.resetFields();
    setCities([]);
    setProductsOffers([]);
  };

  return (
    <Modal
      title={
        <Row align="middle" style={{ padding: 8 }}>
          <Col>
            <img
              src={maxMedal}
              alt="max_medal"
              style={{
                height: '49.5px',
                width: 'auto'
              }}
            />
          </Col>
          <Col flex={1}>{t(`mri_offers_modal.title`)}</Col>
        </Row>
      }
      centered
      open={isOpen}
      onCancel={handleClose}
      footer={false}
      width="900px"
    >
      <Form form={form} layout="vertical" initialValues={defaultFormValues}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              key="address_autocomplete"
              name="address_autocomplete"
              label={t('mri_offers_modal.fields.address_autocomplete.label')}
              hasFeedback={isLoadingAddress}
              {...(isLoadingAddress ? { validateStatus: 'validating' } : {})}
            >
              <Select
                filterOption={false}
                showSearch
                showArrow={false}
                notFoundContent={null}
                onSearch={getAddressAutocomplete}
                onSelect={async (selected) => {
                  const placeId = selected.split('#')[0];
                  await getAddressPlaceId(placeId);
                }}
                options={
                  addressSuggestion &&
                  addressSuggestion.map((predictions) => ({
                    label: predictions.description,
                    value: `${predictions.place_id}#${predictions.description}`
                  }))
                }
                autoComplete="anyrandomstring"
              />
            </Form.Item>
          </Col>
        </Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 16
          }}
        >
          <Form.Item
            name="zipcode"
            label={t('mri_offers_modal.fields.zipcode.label')}
            validateStatus={null}
            rules={[
              {
                required: true,
                message: 'Code postal invalide',
                validator: (_, value) => {
                  if (
                    (zipcodeValidator(value) &&
                      !citiesLoading &&
                      cities.length > 0) ||
                    !form.isFieldTouched('zipcode')
                  ) {
                    return Promise.resolve();
                  }

                  return Promise.reject();
                }
              }
            ]}
            style={{ flex: 1 }}
          >
            <Input
              placeholder={t('mri_offers_modal.fields.zipcode.placeholder')}
            />
          </Form.Item>

          <Form.Item
            name="city"
            label={t('mri_offers_modal.fields.city.label')}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const zipcode = form.getFieldValue('zipcode');
                  if (zipcodeValidator(zipcode) && cities.length > 0) {
                    if (!value) {
                      return Promise.reject();
                    }
                  }

                  return Promise.resolve();
                }
              }
            ]}
            style={{ flex: 1 }}
          >
            <Select
              placeholder={t('mri_offers_modal.fields.city.placeholder')}
              options={cities}
              defaultActiveFirstOption
              style={{ minWidth: 200 }}
            />
          </Form.Item>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="number"
              required
              label={t('mri_offers_modal.fields.number.label')}
              style={{ flex: 1 }}
            >
              <InputNumber
                placeholder={t('mri_offers_modal.fields.number.placeholder')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="street"
              label={t('mri_offers_modal.fields.street.label')}
              required
              style={{ flex: 1 }}
            >
              <Input
                placeholder={t('mri_offers_modal.fields.street.placeholder')}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="is_vacant"
              label={t('mri_offers_modal.fields.is_vacant.label')}
              rules={[{ required: true }]}
              wrapperCol={{ span: 12, offset: 6 }}
            >
              <Radio.Group>
                <Radio value>
                  {t('mri_offers_modal.fields.is_vacant.options.yes')}
                </Radio>
                <Radio value={false}>
                  {t('mri_offers_modal.fields.is_vacant.options.no')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="surface_area"
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {t('mri_offers_modal.fields.surface_area.label')}
                  <span style={{ fontSize: 10, color: 'grey' }}>
                    {t('mri_offers_modal.fields.surface_area.helper')}
                  </span>
                </div>
              }
              rules={[{ required: true }]}
            >
              <InputNumber
                placeholder={t(
                  'mri_offers_modal.fields.surface_area.placeholder'
                )}
                addonAfter="m²"
                min={1}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <div style={{ marginTop: 8 }}>
              <Typography.Text strong>
                {t('mri_offers_modal.fields.building_type.label')}
              </Typography.Text>
              <div
                style={{
                  marginTop: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <Typography.Text>
                  <CheckOutlined style={{ color: 'green', marginRight: 6 }} />
                  {t('mri_offers_modal.building_types.FULL_HOUSE')}
                </Typography.Text>
                <Typography.Text>
                  <CheckOutlined style={{ color: 'green', marginRight: 6 }} />
                  {t(
                    'mri_offers_modal.building_types.HOUSE_WITH_MORE_HALF_OFFICE'
                  )}
                </Typography.Text>
                <Typography.Text>
                  <CheckOutlined style={{ color: 'green', marginRight: 6 }} />
                  {t('mri_offers_modal.building_types.FULL_OFFICE')}
                </Typography.Text>
              </div>
            </div>
          </Col>

          <Col span={12}>
            <Form.Item
              name="specific_risk"
              label={t('mri_offers_modal.fields.specific_risk.label')}
              rules={[{ required: true }]}
              wrapperCol={{ span: 12, offset: 6 }}
              tooltip={t('mri_offers_modal.fields.specific_risk.tooltip')}
            >
              <Radio.Group>
                <Radio value>
                  {t('mri_offers_modal.fields.specific_risk.options.yes')}
                </Radio>
                <Radio value={false}>
                  {t('mri_offers_modal.fields.specific_risk.options.no')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Row justify="start" gutter={5} wrap>
        {productsOffers.map((offer) => {
          return (
            <Col key={offer.type} span={12}>
              <OfferCard
                offer={offer}
                values={valuesWatcher}
                isLoading={estimationLoading}
                isBetter={!!offer.isBetter}
              />
            </Col>
          );
        })}
      </Row>
    </Modal>
  );
};

export default MRIMultiOffersModal;
